import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";

/**
 * OIDC configuration for the Keycloak `sportea` realm.
 *
 * Required env vars (.env / VITE_*):
 *   VITE_OIDC_AUTHORITY  e.g. https://auth.elmv.be/realms/sportea
 *   VITE_OIDC_CLIENT_ID  e.g. sportea-spa
 */
const authority =
  import.meta.env.VITE_OIDC_AUTHORITY ?? "https://auth.elmv.be/realms/sportea";
const clientId = import.meta.env.VITE_OIDC_CLIENT_ID ?? "sportea-spa";
const redirectUri = `${window.location.origin}/auth/callback`;
const postLogoutRedirectUri = window.location.origin;

export const userManager = new UserManager({
  authority,
  client_id: clientId,
  redirect_uri: redirectUri,
  post_logout_redirect_uri: postLogoutRedirectUri,
  response_type: "code",
  scope: "openid profile email",
  loadUserInfo: true,
  automaticSilentRenew: true,
  // The refresh token never reaches JavaScript:
  // oidc-client-ts uses a hidden iframe for silent renew against Keycloak.
  // localStorage (not sessionStorage) so the auth state is shared between
  // tabs — required for flows that open the app in a new tab (e.g. opening
  // a member's sheet from a cercle's Membres tab).
  userStore: new WebStorageStateStore({ store: window.localStorage }),
});

export async function getUser(): Promise<User | null> {
  return userManager.getUser();
}

export async function login(): Promise<void> {
  return userManager.signinRedirect();
}

export async function logout(): Promise<void> {
  return userManager.signoutRedirect();
}

let revokedLogoutStarted = false;

/**
 * Sign out a user whose app account was revoked (deleted in the app) while
 * their Keycloak session was still alive. Order matters: clear the stored
 * tokens FIRST so the post-logout return to "/" boots unauthenticated —
 * otherwise the stale, not-yet-expired access token re-authenticates the now
 * account-less user, GET /api/me returns 404 again, and the logout loops (with
 * automaticSilentRenew hammering Keycloak in parallel). The captured id_token
 * is passed as id_token_hint so Keycloak ends the session without a prompt.
 * Runs at most once per page load.
 */
export async function logoutRevoked(): Promise<void> {
  if (revokedLogoutStarted) return;
  revokedLogoutStarted = true;

  let idToken: string | undefined;
  try {
    idToken = (await userManager.getUser())?.id_token ?? undefined;
    await userManager.removeUser();
  } catch {
    // best-effort — still attempt to end the Keycloak session below
  }

  try {
    await userManager.signoutRedirect(
      idToken ? { id_token_hint: idToken } : undefined,
    );
  } catch {
    // If ending the Keycloak session fails, the local tokens are already gone —
    // a plain reload boots unauthenticated and lands on the login page.
    window.location.assign(window.location.origin);
  }
}

export async function handleLoginCallback(): Promise<User> {
  return userManager.signinRedirectCallback();
}
